/*
* @ngdoc Service
* @name app.services.SessionService
* @description
* This service is used for handling user related functions
*/

import { Injectable } from '@angular/core';
import { CommonHttpService } from './common-http.service';
import { appApiUrl } from '@configs/app-api-urls.config';
import { ApiResponseModel } from '@shared/models/api-response-model';
import { Observable } from 'rxjs';
import { SessionModel } from '@app/modules/shared/models/session.model';
import { SessionRating } from '@app/modules/shared/models/session-rating';
import { SessionFilter, SessionStateEnum } from '../helpers/enums/enums';


@Injectable()
export class SessionService {

  constructor(private commonHttpService: CommonHttpService) { }

  /**
   * Create new session
   * @param sessionO 
   */
  createSession(sessionO: SessionModel): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.session.default, sessionO);
  }

  /**
   * Get Session Listing
   */
  getSessions(skip: number, take: number, search: any, type: SessionFilter, startDate: number, endDate: number): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.default}?skip=${skip}&take=${take}&search=${search}&type=${type}&startDate=${startDate}&endDate=${endDate}`);
  }

  /**
   * get session on refresh
   * @param skip 
   * @param take 
   * @param search 
   * @param type 
   * @param startDate 
   * @param endDate 
   * @returns 
   */
  getSessionsAutoRefresh(skip: number, take: number, search: any, type: SessionFilter, startDate: number, endDate: number): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.default}?skip=${skip}&take=${take}&search=${search}&type=${type}&startDate=${startDate}&endDate=${endDate}=skipLoader`);
  }
  /**\
   * get session by id
   */
  getSessionById(sessionId: string): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.default}/${sessionId}`);
  }

  /**
   * save/download recording
   * @param key 
   */
  saveRecording(key: string, type: string) {
    return this.commonHttpService.getFile(`${appApiUrl.recording.default}/download/${key}?type=${type}`);
  }
  /**
   * get recording url
   * @param key 
   * @param type 
   */
  getRecordingUrl(key: string, type: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.recording.default}/download/${key}?type=${type}`);
  }

  /**
   * delete session
   * @param sessionId 
   */
  delete(sessionId: string) {
    return this.commonHttpService.delete<ApiResponseModel>(`${appApiUrl.session.default}/${sessionId}`);
  }
  /**
   * update state of session
   * @param sessionId 
   * @param isLive 
   */
  updateLiveState(sessionId: string, isLive: boolean) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.default}/live-state/${sessionId}/${isLive}`)
  }
  /**
   * rating of session
   * @param sessionO 
   */
  saveRating(sessionRateO: SessionRating): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.session.rating, sessionRateO);
  }

  /**
   * get conference setting
   * @param sessionId 
   * @returns 
   */
  getConferenceSetting(sessionId: string) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.confSetting}/${sessionId}`)
  }


  /**
   * get session by id guest
   * @param sessionId 
   * @param guestId 
   * @returns 
   */
  getSessionByIdGuest(sessionId: string, guestId: string = ''): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.guest}/${sessionId}/${guestId}`);
  }


  /**
   * get session by id for guest room
   * @param sessionId 
   * @returns 
   */
  getSessionByIdForGuestRoom(sessionId: string, guestId: string = ''): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.guestRoom}/${sessionId}/${guestId}`);
  }

  /**
   * get session by id for host room
   * @param sessionId 
   * @returns 
   */
  getSessionByIdForHostRoom(sessionId: string): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.hostRoom}/${sessionId}`);
  }

  /**
   * update status of live video
   * @param sessionId 
   * @param state 
   * @returns 
   */
  updateState(sessionId: string, state: SessionStateEnum) {
    return this.commonHttpService.get<ApiResponseModel>(`${appApiUrl.session.default}/state/${sessionId}/${state}`)
  }

  /**
   * share files
   * @param shareFile 
   * @returns 
   */
  shareFiles(shareFile: any): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.session.shareFiles, shareFile);
  }

  /**
   * get shared files to accessor
   * @param tokenO 
   * @returns 
   */
  getShareFiles(tokenO: any): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.session.getSharedFiles, tokenO);
  }

  /**
   * update live chat meta
   * @param metaO 
   * @returns 
   */
  updateLiveChatMeta(metaO: { sessionId: string, meta: string }): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(appApiUrl.session.chatMeta, metaO);
  }


  saveWaitingParticipant(sessionAndConfId, sessionId, waitingParticipant: any): Observable<ApiResponseModel> {
    return this.commonHttpService.post<ApiResponseModel>(`${appApiUrl.session.waitingParticipant}/${sessionAndConfId}`, {
      sessionId,
      waiting: waitingParticipant
    });
  }

  getConferenceState(conferenceId: string): Observable<ApiResponseModel> {
    return this.commonHttpService.get<ApiResponseModel>(appApiUrl.session.conferenceState + conferenceId);
  }


}
